import { FieldType } from "@/enums";
import { getFlatFormFields } from "@/helpers";
import { useGetAdminFlatFormsQuery } from "@/services";
import { arrayOf, element, object } from "prop-types";
import { createContext, useContext, useEffect, useRef, useState } from "react";

export const DynamicFormContext = createContext();
export const useDynamicFormContext = () => useContext(DynamicFormContext);

export const DynamicFormProvider = ({ children, config, initialValues }) => {
  const formRef = useRef(null);

  const [formData, setFormData] = useState(initialValues || {});
  const [flatFormFields, setFlatFormFields] = useState([]);
  const [currentStatus, setCurrentStatus] = useState(null);
  const [currentProductCategory, setCurrentProductCategory] = useState(null);

  const { data: { flatForms } = {} } = useGetAdminFlatFormsQuery();

  useEffect(() => {
    if (config) {
      setFlatFormFields(getFlatFormFields(config));
    }
  }, [config]);

  useEffect(() => {
    setCurrentStatus(
      formRef.current?.getFieldValue("status") || formData.status
    );
    setCurrentProductCategory(
      formRef.current?.getFieldValue("productCategory") || formData.businessUnit
    );
  }, [formData.businessUnit, formData.status, formRef]);

  /***
   * handleValuesChange will update the form data anytime a form value changes.
   * @param changedValues: the values that changed on the form
   */
  const handleValuesChange = (changedValues) => {
    const updatedForm = { ...formData };
    let didStuff = false;

    for (const propertyName in changedValues) {
      if (
        Object.prototype.hasOwnProperty.call(changedValues, propertyName) &&
        updatedForm[propertyName] !== changedValues[propertyName]
      ) {
        const formElement = getPropertyFieldByName(propertyName);
        const childElement = getChildProperties(formElement?.property?.id);
        const isVisible = showFormFieldByDependentProperty(
          childElement?.property?.dependsOnPropertyId,
          [updatedForm[propertyName]]
        );

        if (!isVisible && childElement?.property?.name) {
          switch (childElement.property.fieldType) {
            case "MultiSelectDropDown":
              formRef.current.setFieldsValue({
                [childElement.property.name]: [],
              });
              break;
            case "DropDown":
            case "Date":
            case "Text":
            case "TextArea":
            case "Number":
              formRef.current.setFieldsValue({
                [childElement.property.name]: null,
              });
              break;
            default:
              break;
          }
        }
        updatedForm[propertyName] = changedValues[propertyName];
        didStuff = true;
      }
    }

    //this is protection to reduce the number of page redraws
    if (didStuff) {
      setFormData(updatedForm);
    }
  };

  const getPropertyField = (propertyId) =>
    flatFormFields.find(({ property }) => property.id === propertyId);

  const getPropertyFieldByName = (name) =>
    flatFormFields.find(({ property }) => property.name === name);

  const getChildProperties = (propertyId) =>
    flatFormFields.find(
      ({ property }) => property?.dependsOnPropertyId === propertyId
    );

  const getDependentPropertyValue = (dependentPropertyId) => {
    const dependentProperty = getPropertyField(dependentPropertyId);
    if (dependentProperty) {
      return formRef.current
        ? formRef.current.getFieldValue(dependentProperty.property.name)
        : formData[dependentProperty.name];
    }
  };

  const getDependentPropertyField = (dependentPropertyId) =>
    getPropertyField(dependentPropertyId);

  const showFormFieldByDependentProperty = (
    dependsOnPropertyId,
    dependsOnPropertyValues
  ) => {
    let showFormField = true;

    if (!dependsOnPropertyId) return showFormField;

    do {
      let dependentFormFieldValue =
        getDependentPropertyValue(dependsOnPropertyId);

      const dependentFormField = getDependentPropertyField(dependsOnPropertyId);

      //if it is a formfieldType of switch making sure default values as false when it is unchecked
      //and parse it to string so it can easily be validated
      if (
        dependentFormField?.property?.fieldType === FieldType.Switch &&
        dependentFormFieldValue
      ) {
        dependentFormFieldValue = dependentFormFieldValue
          .toString()
          .toLowerCase();
      }

      showFormField =
        dependsOnPropertyValues?.includes(dependentFormFieldValue) ||
        (!dependsOnPropertyValues?.length && dependentFormFieldValue);

      dependsOnPropertyId = dependentFormField?.dependsOnPropertyId;
    } while (dependsOnPropertyId && showFormField);

    return showFormField;
  };

  return (
    <DynamicFormContext.Provider
      value={{
        handleValuesChange,
        getDependentPropertyValue,
        getDependentPropertyField,
        showFormFieldByDependentProperty,
        currentStatus,
        currentProductCategory,
        formConfig: config,
        formRef,
        formData,
        initialValues,
        flatForms,
      }}
    >
      {children}
    </DynamicFormContext.Provider>
  );
};

DynamicFormProvider.propTypes = {
  children: element,
  config: arrayOf(object),
  /**
   * Not sure if {initialValues} can more strongly typed considering
   * the dynamic nature of this component
   */
  initialValues: object,
};
